import React, { useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";
import { formatDate } from "../../utils/Helper";

/**
 * Unified transaction list for the wallet screen (Stripe). Renders the flat,
 * newest-first `entries` from `/me/statement` as ONE list with tabs — All /
 * Earned / Spent / Withdrawn. 'All' is the full chronological ledger; each other
 * tab filters `entries` to `entry.category === <tab>`. Tabs with zero rows are
 * hidden (derived from the categories present in the data — no role logic). The
 * active tab is local component state. Display-only.
 */

const CATEGORY_TABS = [
  { id: "earned", labelId: "EARNED" },
  { id: "spent", labelId: "SPENT" },
  { id: "withdrawn", labelId: "WITHDRAWN" },
];

const EntryTitle = ({ title }) =>
  title?.id ? <FormattedMessage id={title.id} /> : <>{title?.text || ""}</>;

const TransactionRow = ({ entry }) => {
  const isIn = entry.direction === "IN";
  const amount = entry.amount.toFixed(2);
  return (
    <div
      className="d-flex justify-content-between align-items-center py-2 border-bottom"
      data-testid="wallet-txn-row"
    >
      <div className="d-flex flex-column">
        <span className="fw-bold text-truncate">
          <EntryTitle title={entry.title} />
        </span>
        {entry.contractId != null && (
          <span className="text-muted small" data-testid="wallet-txn-contract">
            <FormattedMessage
              id="WALLET_CONTRACT_ID"
              values={{ id: entry.contractId }}
            />
          </span>
        )}
        {entry.date && (
          <span className="text-muted small">{formatDate(entry.date)}</span>
        )}
        {/* Receipts belong to money going OUT — an IN row is someone else's
            payment, so its receipt is not the viewer's to show (matches mobile). */}
        {!isIn && entry.receiptUrl && (
          <a
            href={entry.receiptUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="small"
            data-testid="wallet-txn-receipt"
          >
            <FormattedMessage id="WALLET_RECEIPT" />
          </a>
        )}
      </div>
      <span className={isIn ? "fw-bold text-success" : "fw-bold text-danger"}>
        {isIn ? "+" : "−"} ${amount}
      </span>
    </div>
  );
};

const EarnedTrail = ({ entries = [] }) => {
  // Which category tabs have at least one row — hide the empty ones.
  const availableTabs = useMemo(() => {
    const present = new Set(entries.map((e) => e.category));
    return CATEGORY_TABS.filter((tab) => present.has(tab.id));
  }, [entries]);

  const [activeTab, setActiveTab] = useState("all");

  // If the active category tab drops out (data changed), fall back to All.
  const effectiveTab =
    activeTab === "all" || availableTabs.some((t) => t.id === activeTab)
      ? activeTab
      : "all";

  const visibleEntries =
    effectiveTab === "all"
      ? entries
      : entries.filter((e) => e.category === effectiveTab);

  const tabButton = (id, labelId) => (
    <button
      key={id}
      type="button"
      onClick={() => setActiveTab(id)}
      className={`btn btn-sm ${
        effectiveTab === id ? "btn-warning" : "btn-outline-secondary"
      }`}
      data-testid={`wallet-tab-${id}`}
    >
      <FormattedMessage id={labelId} />
    </button>
  );

  return (
    <div className="w-100 p-4 shadow border mt-4" data-testid="wallet-txn-list">
      <div className="d-flex gap-2 mb-3 flex-wrap">
        {tabButton("all", "ALL")}
        {availableTabs.map((tab) => tabButton(tab.id, tab.labelId))}
      </div>
      {visibleEntries.length === 0 ? (
        <div className="text-muted text-center py-3">
          <FormattedMessage id="NO_TRANSACTIONS" />
        </div>
      ) : (
        visibleEntries.map((entry) => (
          <TransactionRow key={entry.key} entry={entry} />
        ))
      )}
    </div>
  );
};

export default React.memo(EarnedTrail);
